import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  Table
} from 'react-bootstrap';
import { http } from '../../features/jobs/api/http';
import { fetchJob } from '../../features/jobs/api/jobsApi';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import ProductColorDot from '../../components/common/ProductColorDot';
import { API } from '../../config/api';
import { PHASE_LABEL } from './ProjectJobsMonitor';
import { formatProductLabel } from '../../helpers/productLabel';

const toNum = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Number(n || 0).toLocaleString();

const colorFromKey = key => {
  const parts = String(key || '').split('|');
  const up = new Set(CABLE_COLORS.map(c => c.toUpperCase()));
  return parts.find(seg => up.has(String(seg).trim().toUpperCase())) || '';
};

// Phases that are parallel per conductor: the branch holds N drums of the SAME
// length, one per conductor colour, and the product length is one drum's draw —
// not their sum. This is also the case where a partial selection quietly
// produces an unbuildable target (see the warning below).
const PARALLEL_PHASES = new Set(['conductor_isolation', 'unwinding']);

// TRANSFORM — re-plan a launched product into a different one.
//
// Three operations behind one screen, picked from the state of the selected
// drums (the wizard does not ask, because the answer is never the planner's to
// give):
//   total                     → convert-product      (in place, same job)
//   partial, drums produced   → fork-product         (cut the drums, new job)
//   partial, drums still queued → fork-product-merge (merge-execution pair)
const TransformWizzard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const sourceJobId = location?.state?.sourceJobId || '';
  const navRows = Array.isArray(location?.state?.rows) ? location.state.rows : [];
  const returnTo =
    location?.state?.returnTo || '/apps/project-management/project-monitor';

  const [srcJob, setSrcJob] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [submitErr, setSubmitErr] = useState('');
  const [busy, setBusy] = useState(false);
  // set when the transform succeeded but the backend's consistency audit found
  // something — the hand-off to plan-next-phase waits behind it
  const [resultWarnings, setResultWarnings] = useState([]);
  const [pendingRows, setPendingRows] = useState(null);

  // Every drum that arrived from Project Token State, de-duplicated. They are
  // all listed, and all start ticked — the planner picked them on the previous
  // screen, so unticking here is a correction, not a second selection.
  const allDrums = useMemo(() => {
    const seen = new Set();
    return navRows
      .filter(r => r?.tokenId && !seen.has(r.tokenId) && seen.add(r.tokenId))
      .map(r => {
        const produced = toNum(r.producedLength);
        // PENDING = still queued, not physically made → merge-execution path.
        const isPending =
          produced <= 0 && String(r.status || '').toUpperCase() !== 'DONE';
        return {
          tokenId: r.tokenId,
          phase: r.phase,
          color: r.color ?? null,
          available: toNum(r.availableLength ?? r.producedLength),
          planned: toNum(r.plannedLength),
          produced,
          status: String(r.status || '').toUpperCase() || 'PENDING',
          isPending
        };
      });
  }, [navRows]);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  useEffect(() => {
    setSelectedIds(new Set(allDrums.map(d => d.tokenId)));
  }, [allDrums]);

  const branchDrums = useMemo(
    () => allDrums.filter(d => selectedIds.has(d.tokenId)),
    [allDrums, selectedIds]
  );

  const toggle = tokenId =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(tokenId)) next.delete(tokenId);
      else next.add(tokenId);
      return next;
    });

  const branchPhase = branchDrums[0]?.phase || allDrums[0]?.phase || '';
  const mixedPhase = branchDrums.some(d => d.phase !== branchPhase);
  const branchIsPending =
    branchDrums.length > 0 && branchDrums.every(d => d.isPending);
  // Parallel branch drums share the product length → the forkable product
  // length is the MIN forkable across them (planned for pending, else avail).
  const maxFork = branchDrums.length
    ? Math.min(...branchDrums.map(d => (d.isPending ? d.planned : d.available) || 0))
    : 0;

  const [mode, setMode] = useState('total'); // 'total' (convert) | 'partial' (split)
  const [forkLength, setForkLength] = useState('');
  const [target, setTarget] = useState({
    name: '',
    numberOfConductors: '',
    section: '',
    color: ''
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!sourceJobId) {
        setLoadErr('Open this from Project Token State: select produced drums of one phase → “Transform / Fork”.');
        return;
      }
      try {
        const job = await fetchJob(sourceJobId);
        if (!alive) return;
        setSrcJob(job);
        setTarget(t => ({
          ...t,
          name: job?.name ?? t.name,
          numberOfConductors: job?.numberOfConductors ?? t.numberOfConductors,
          section: job?.section ?? t.section,
          color: colorFromKey(job?.source?.launch?.productKey) || t.color
        }));
      } catch (e) {
        if (alive) setLoadErr(e?.message || String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceJobId]);

  const srcColor = colorFromKey(srcJob?.source?.launch?.productKey);
  const isChange =
    String(target.name).trim().toUpperCase() !== String(srcJob?.name || '').toUpperCase() ||
    toNum(target.numberOfConductors) !== toNum(srcJob?.numberOfConductors) ||
    toNum(target.section) !== toNum(srcJob?.section) ||
    String(target.color).trim().toUpperCase() !== String(srcColor).toUpperCase();

  const targetValid =
    String(target.name).trim() &&
    String(target.color).trim() &&
    toNum(target.numberOfConductors) > 0 &&
    toNum(target.section) > 0;

  // ── the target recipe, resolved live ───────────────────────────────────────
  // "Which product does this become" was a question the screen never answered:
  // it showed four form fields and nothing about what they add up to. Resolving
  // the standard as soon as the four are set means the phase chain, the
  // conductor colours and — crucially — how many conductors the target needs
  // are on screen BEFORE submitting, not discovered afterwards.
  const [targetStd, setTargetStd] = useState(null);
  const [stdErr, setStdErr] = useState('');
  const [stdBusy, setStdBusy] = useState(false);

  useEffect(() => {
    if (!targetValid || !srcJob) {
      setTargetStd(null);
      setStdErr('');
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setStdBusy(true);
      setStdErr('');
      try {
        const std = await resolveTarget();
        if (alive) setTargetStd(std);
      } catch (e) {
        if (alive) {
          setTargetStd(null);
          setStdErr(e?.message || String(e));
        }
      } finally {
        if (alive) setStdBusy(false);
      }
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.name, target.numberOfConductors, target.section, target.color, targetValid, srcJob]);

  // The conductor colours the target recipe expects at the branch phase — the
  // list the selection below is measured against.
  const targetBranchColors = useMemo(() => {
    const ph = (targetStd?.phases || []).find(
      p => (p.name || p.phase) === branchPhase
    );
    if (!ph || !Array.isArray(ph.conductors)) return null;
    return ph.conductors.map(c => c.isolation?.color).filter(Boolean);
  }, [targetStd, branchPhase]);

  // The heart of problem 1: forking a SUBSET of parallel conductors produces a
  // job that owns one drum but whose recipe wants N, so the rope can never be
  // planned and there is no way back. The operation is still allowed — it is
  // occasionally what you want — but it is no longer silent.
  const parallelBranch = PARALLEL_PHASES.has(branchPhase);
  const neededCount = targetBranchColors
    ? targetBranchColors.length
    : toNum(target.numberOfConductors);
  const incompleteSelection =
    mode === 'partial' &&
    parallelBranch &&
    neededCount > 0 &&
    branchDrums.length > 0 &&
    branchDrums.length < neededCount;

  const missingColors = useMemo(() => {
    if (!incompleteSelection || !targetBranchColors) return [];
    const have = new Set(
      branchDrums.map(d => String(d.color || '').toUpperCase())
    );
    return targetBranchColors.filter(c => !have.has(String(c).toUpperCase()));
  }, [incompleteSelection, targetBranchColors, branchDrums]);

  // ── the orders this job owes ───────────────────────────────────────────────
  // A transform changes what the product IS or how much of it exists, so
  // anything already promised has to be re-decided. Nothing is assumed: every
  // line defaults to "leave it", and the planner says what happens to each.
  const [allocCtx, setAllocCtx] = useState(null);
  const [allocErr, setAllocErr] = useState('');
  const [allocBusy, setAllocBusy] = useState(false);
  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    if (!sourceJobId) return undefined;
    let alive = true;
    (async () => {
      setAllocBusy(true);
      setAllocErr('');
      try {
        const { data } = await http.get(
          `/api/redis/jobs/${sourceJobId}/allocation-context`
        );
        if (!alive) return;
        setAllocCtx(data);
        const seed = {};
        for (const l of data?.lines || []) {
          // start from what the job promises today, per delivery item
          const keep = {};
          for (const it of l.deliveryItems || []) keep[it.itemId] = toNum(it.reserved);
          seed[`${l.orderId}::${l.lineId}`] = {
            action: 'KEEP',
            rewriteLine: true,
            keep,
            reduceMeters: 0
          };
        }
        setDecisions(seed);
      } catch (e) {
        if (alive) setAllocErr(e?.response?.data?.error || e?.message || String(e));
      } finally {
        if (alive) setAllocBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [sourceJobId]);

  const allocLines = allocCtx?.lines || [];
  const setDecision = (key, patch) =>
    setDecisions(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // A fork NEVER carries the allocation over: the new product is a different
  // cable and cannot fulfil a line that asked for the old one, so it starts as
  // stock. What the wizard decides is how much of its promise the SOURCE keeps,
  // now that it can make less.
  const allocationDecisions = useMemo(() => {
    const out = [];
    for (const l of allocLines) {
      const k = `${l.orderId}::${l.lineId}`;
      const d = decisions[k];
      if (!d) continue;

      if (mode === 'total') {
        if (d.action === 'FOLLOW') {
          out.push({
            orderId: l.orderId,
            lineId: l.lineId,
            action: 'KEEP',
            rewriteLine: true
          });
        } else if (d.action === 'RELEASE') {
          out.push({ orderId: l.orderId, lineId: l.lineId, action: 'RELEASE' });
        }
        continue;
      }

      // Itemised promise → decide it in units (which drums, which coils).
      if (toNum(l.reservedUnits) > 0) {
        const keep = d.keep || {};
        const changed = (l.deliveryItems || []).some(
          it => toNum(keep[it.itemId]) !== toNum(it.reserved)
        );
        if (changed) {
          out.push({
            orderId: l.orderId,
            lineId: l.lineId,
            action: 'TRIM',
            keepReservation: keep
          });
        }
        continue;
      }

      // No itemised promise (the split is still decided at the final phase) —
      // metres are the only statement there is.
      const m = toNum(d.reduceMeters);
      if (m > 0) {
        out.push({
          orderId: l.orderId,
          lineId: l.lineId,
          action: 'REDUCE',
          meters: m
        });
      }
    }
    return out;
  }, [allocLines, decisions, mode]);

  // After a partial fork the source can only make what is left behind, and
  // everything it still promises has to fit inside that. Leaving it over-
  // promised is what made the order read 20,000 / 20,000 when only half of it
  // could still be produced.
  const capacityAfter =
    mode === 'partial'
      ? Math.max(0, toNum(srcJob?.plannedLength) - toNum(forkLength))
      : toNum(srcJob?.plannedLength);

  const promisedAfter = useMemo(() => {
    let total = 0;
    for (const l of allocLines) {
      const d = decisions[`${l.orderId}::${l.lineId}`] || {};
      if (mode === 'total') {
        total += d.action === 'RELEASE' ? 0 : toNum(l.allocatedFromThisJob);
      } else if (toNum(l.reservedUnits) > 0) {
        for (const it of l.deliveryItems || []) {
          total += toNum((d.keep || {})[it.itemId]) * toNum(it.lengthPerUnit);
        }
      } else {
        total += Math.max(
          0,
          toNum(l.allocatedFromThisJob) - toNum(d.reduceMeters)
        );
      }
    }
    return total;
  }, [allocLines, decisions, mode]);

  const overPromised = Math.max(0, promisedAfter - capacityAfter);

  // Scale every promise down to what the source can still deliver. Floor per
  // item, so it can only ever land under the cap, never over.
  const autoTrim = () => {
    const ratio = promisedAfter > 0 ? Math.min(1, capacityAfter / promisedAfter) : 1;
    setDecisions(prev => {
      const next = { ...prev };
      for (const l of allocLines) {
        const k = `${l.orderId}::${l.lineId}`;
        const d = { ...(next[k] || {}) };
        if (toNum(l.reservedUnits) > 0) {
          const keep = {};
          for (const it of l.deliveryItems || []) {
            keep[it.itemId] = Math.floor(toNum((d.keep || {})[it.itemId]) * ratio);
          }
          d.keep = keep;
        } else {
          const owed = toNum(l.allocatedFromThisJob);
          d.reduceMeters = Math.max(0, Math.round(owed - owed * ratio));
        }
        next[k] = d;
      }
      return next;
    });
  };

  const partialValid =
    mode === 'total' ||
    (!mixedPhase && toNum(forkLength) > 0 && toNum(forkLength) <= maxFork);

  const canSubmit =
    !!sourceJobId &&
    branchDrums.length > 0 &&
    targetValid &&
    isChange &&
    partialValid &&
    // Refusing rather than guessing: a source that still owes more than it can
    // make is the exact state that made the order say 20,000 / 20,000 when half
    // the cable had gone to a different product.
    overPromised <= 0 &&
    !busy;

  // resolve the target standard (phases + materials) — shared by convert + fork.
  async function resolveTarget() {
    const payload = [
      {
        name: String(target.name).trim(),
        numberOfConductors: toNum(target.numberOfConductors),
        section: toNum(target.section),
        length: toNum(srcJob.plannedLength),
        color: String(target.color).trim(),
        hasMixedConductorSections: false,
        greenYellowSection: ''
      }
    ];
    const resp = await axios.post(
      `${API.BACKEND}/api/v1/standard/find-many`,
      payload
    );
    const standards = resp?.data?.data || [];
    const std = standards.find(
      s =>
        String(s.name).trim().toUpperCase() ===
          String(target.name).trim().toUpperCase() &&
        toNum(s.numberOfConductors) === toNum(target.numberOfConductors) &&
        toNum(s.section) === toNum(target.section) &&
        String(s.color || '').trim().toUpperCase() ===
          String(target.color).trim().toUpperCase()
    );
    if (!std) {
      throw new Error(
        `No standard found for ${formatProductLabel(target)} ${target.color}`
      );
    }
    const phases = (std.phases || []).map((phase, i, arr) => ({
      ...phase,
      dependsOn: i > 0 ? arr[i - 1].name : null
    }));
    return {
      standardId: std._id || std.standardId || null,
      name: std.name,
      numberOfConductors: toNum(std.numberOfConductors),
      section: toNum(std.section),
      color: std.color || target.color,
      phases
    };
  }

  const goPlanDownstream = rows =>
    navigate('/apps/project-management/plan-next-phase-wizard', {
      state: {
        from: 'TransformWizzard',
        tokenIds: rows.map(r => r.tokenId),
        rows,
        returnTo
      }
    });

  // The backend audits the product's four statements before it writes and hands
  // back anything that did not add up. Redirecting straight to the next wizard
  // would bury that — the planner is the only one who can act on it, so the
  // hand-off waits until they have seen it.
  const finish = (warnings, rows) => {
    if (Array.isArray(warnings) && warnings.length) {
      setResultWarnings(warnings);
      setPendingRows(rows || []);
      setBusy(false);
      return;
    }
    if (rows && rows.length) goPlanDownstream(rows);
    else navigate(returnTo);
  };

  // TOTAL — convert the whole product in place (keeps tasks/tokens + length).
  const handleConvert = async () => {
    const tgt = targetStd || (await resolveTarget());
    const convResp = await http.post('/api/redis/jobs/convert-product', {
      sourceJobId,
      target: tgt,
      allocationDecisions
    });
    if (!convResp?.data?.success)
      throw new Error(convResp?.data?.error || 'convert failed');
    finish(
      convResp?.data?.warnings,
      branchDrums.length ? navRows.filter(r => selectedIds.has(r.tokenId)) : null
    );
  };

  // PARTIAL — fork `forkLength` of the branch drums into a NEW target job; the
  // source keeps the remainder. Downstream planned on the new fork job.
  const handleFork = async () => {
    const tgt = targetStd || (await resolveTarget());
    const len = toNum(forkLength);

    if (branchIsPending) {
      const forkResp = await http.post('/api/redis/jobs/fork-product-merge', {
        sourceJobId,
        branchPhase,
        target: tgt,
        forkLength: len,
        allocationDecisions
      });
      const data = forkResp?.data;
      if (!data?.forkJobId) throw new Error(data?.error || 'fork-merge failed');
      const rows = branchDrums.map(d => ({
        jobId: data.forkJobId,
        tokenId: d.tokenId,
        phase: branchPhase,
        color: d.color ?? null,
        plannedLength: len,
        availableLength: 0,
        producedLength: 0,
        status: 'PENDING',
        productLabel: data.productLabel || tgt.name
      }));
      finish(data.warnings, rows);
      return;
    }

    // Every selected drum contributes the SAME length: parallel conductors each
    // give `forkLength` metres to make `forkLength` metres of cable. Per-drum
    // lengths would desynchronise the conductors, so there is one figure.
    const links = branchDrums.map(d => ({
      sourceTokenId: d.tokenId,
      length: len,
      color: d.color
    }));
    const forkResp = await http.post('/api/redis/jobs/fork-product', {
      sourceJobId,
      branchPhase,
      target: tgt,
      links,
      forkLength: len,
      allocationDecisions
    });
    const data = forkResp?.data;
    if (!data?.forkJobId) throw new Error(data?.error || 'fork failed');
    const rows = (data.injected || []).map(inj => ({
      jobId: data.forkJobId,
      tokenId: inj.tokenId,
      phase: branchPhase,
      color: inj.color ?? null,
      producedLength: inj.length,
      availableLength: inj.length,
      productLabel: data.productLabel || tgt.name
    }));
    finish(data.warnings, rows);
  };

  const handleSubmit = async () => {
    setSubmitErr('');
    setBusy(true);
    try {
      if (mode === 'total') await handleConvert();
      else await handleFork();
    } catch (e) {
      setSubmitErr(e?.response?.data?.error || e?.message || String(e));
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <Card className="mb-9 mt-3">
        <Card.Body>
          <h3 className="mb-2">Transform product</h3>
          <Alert variant="warning" className="py-2 mb-0">
            {loadErr}
          </Alert>
        </Card.Body>
      </Card>
    );
  }
  if (!srcJob) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const targetLabel = `${target.name || '—'} ${toNum(
    target.numberOfConductors
  )}x${toNum(target.section)}${target.color ? ` ${target.color}` : ''}`;

  return (
    <Card className="mb-9 mt-3">
      <Card.Header>
        <h3 className="mb-1">Transform product</h3>
        <div className="fs-9 text-700 d-flex align-items-center flex-wrap gap-2">
          Re-plan
          <span className="d-inline-flex align-items-center gap-1 fw-semibold">
            <ProductColorDot colorName={srcColor} />
            {srcJob.productLabel}
            {srcColor ? ` (${srcColor})` : ''}
          </span>
          into a different product. Executed phases and tasks are always
          preserved — a transform never throws away work that is already on a
          drum.
        </div>
      </Card.Header>
      <Card.Body>
        {mixedPhase && (
          <Alert variant="warning" className="py-2">
            The selection mixes phases — the downstream will be planned from{' '}
            <b>{PHASE_LABEL[branchPhase] || branchPhase}</b>.
          </Alert>
        )}
        {submitErr && (
          <Alert variant="danger" className="py-2">
            {submitErr}
          </Alert>
        )}

        {!!resultWarnings.length && (
          <Alert variant="warning" className="py-2">
            <div className="fw-semibold mb-1">
              The transform was applied, but the numbers do not all agree:
            </div>
            <ul className="mb-2 ps-3 fs-9">
              {resultWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
            <div className="fs-10 text-700 mb-2">
              Nothing is lost — this is the product&apos;s promise disagreeing
              with itself. Reconcile it in Sales, or run{' '}
              <code>scripts/resync-allocations.mjs</code>.
            </div>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                pendingRows && pendingRows.length
                  ? goPlanDownstream(pendingRows)
                  : navigate(returnTo)
              }
            >
              {pendingRows && pendingRows.length
                ? 'Continue to plan downstream'
                : 'Back'}
            </Button>
          </Alert>
        )}

        {/* ── 1. the drums ──────────────────────────────────────────────── */}
        <h5 className="mb-1">Drums to transform</h5>
        <p className="text-700 fs-9 mb-2">
          Everything picked on Project Token State, already ticked. Untick a drum
          to leave it on {srcJob.name}.
        </p>
        <Table size="sm" className="fs-9 align-middle mb-2">
          <thead>
            <tr className="text-uppercase text-700 fs-10">
              <th style={{ width: 36 }} />
              <th>Token</th>
              <th>Phase</th>
              <th>Colour</th>
              <th className="text-end">Available</th>
              <th className="text-end">Takes</th>
              <th className="text-end">Stays on {srcJob.name}</th>
              <th>Becomes</th>
            </tr>
          </thead>
          <tbody>
            {allDrums.map(d => {
              const on = selectedIds.has(d.tokenId);
              const pool = d.isPending ? d.planned : d.available;
              const takes = !on ? 0 : mode === 'total' ? pool : toNum(forkLength);
              const stays = Math.max(0, pool - takes);
              return (
                <tr key={d.tokenId} className={on ? '' : 'opacity-50'}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(d.tokenId)}
                      aria-label={`Include ${d.tokenId}`}
                    />
                  </td>
                  <td className="font-monospace">{d.tokenId}</td>
                  <td>{PHASE_LABEL[d.phase] || d.phase}</td>
                  <td>
                    <span className="d-inline-flex align-items-center gap-2">
                      <ProductColorDot colorName={d.color} />
                      {d.color || '—'}
                    </span>
                  </td>
                  <td className="text-end">
                    {fmt(pool)} m
                    {d.isPending && (
                      <Badge bg="warning" className="ms-2 fs-10">
                        queued
                      </Badge>
                    )}
                  </td>
                  <td className="text-end fw-semibold">
                    {on ? `${fmt(takes)} m` : '—'}
                  </td>
                  <td className="text-end">{on ? `${fmt(stays)} m` : `${fmt(pool)} m`}</td>
                  <td>
                    {on ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <ProductColorDot colorName={target.color} />
                        {targetLabel}
                      </span>
                    ) : (
                      <span className="text-700">stays {srcJob.name}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {!branchDrums.length && (
          <Alert variant="warning" className="py-2">
            No drum selected — tick at least one.
          </Alert>
        )}

        {/* ── 2. target ─────────────────────────────────────────────────── */}
        <h5 className="mb-2 mt-4">Target product</h5>
        <Row className="g-3 mb-2">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Target product</Form.Label>
              <Form.Select
                value={target.name}
                onChange={e => setTarget(t => ({ ...t, name: e.target.value }))}
              >
                <option value="">Select product…</option>
                {CABLE_NAMES_NO_ROPE.map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Conductors</Form.Label>
              <Form.Control
                type="number"
                value={target.numberOfConductors}
                onChange={e =>
                  setTarget(t => ({ ...t, numberOfConductors: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Section (mm²)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={target.section}
                onChange={e =>
                  setTarget(t => ({ ...t, section: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Color</Form.Label>
              <Form.Select
                value={target.color}
                onChange={e => setTarget(t => ({ ...t, color: e.target.value }))}
              >
                <option value="">Select color…</option>
                {CABLE_COLORS.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* what those four fields actually add up to */}
        <div className="bg-body-emphasis border rounded-2 p-3 mb-3 fs-9">
          {stdBusy && (
            <span className="text-700">
              <Spinner animation="border" size="sm" className="me-2" />
              Resolving the target recipe…
            </span>
          )}
          {!stdBusy && stdErr && (
            <span className="text-danger">{stdErr}</span>
          )}
          {!stdBusy && !stdErr && !targetStd && (
            <span className="text-700">
              Pick product, conductors, section and colour to see the recipe.
            </span>
          )}
          {!stdBusy && targetStd && (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <ProductColorDot colorName={srcColor} />
                <span className="fw-semibold">
                  {formatProductLabel(srcJob)}
                  {srcColor ? ` ${srcColor}` : ''}
                </span>
                <span className="text-700 fs-8">→</span>
                <ProductColorDot colorName={targetStd.color} />
                <span className="fw-semibold">
                  {formatProductLabel(targetStd)} {targetStd.color}
                </span>
              </div>
              <div className="d-flex align-items-center flex-wrap gap-1">
                <span className="text-700 me-1">Recipe:</span>
                {(targetStd.phases || []).map(p => {
                  const nm = p.name || p.phase;
                  return (
                    <Badge
                      key={nm}
                      bg={nm === branchPhase ? 'primary' : 'secondary'}
                      variant="phoenix"
                      className="fs-10"
                    >
                      {PHASE_LABEL[nm] || nm}
                    </Badge>
                  );
                })}
              </div>
              {targetBranchColors && (
                <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                  <span className="text-700">
                    Needs at {PHASE_LABEL[branchPhase] || branchPhase}:
                  </span>
                  {targetBranchColors.map((c, i) => (
                    <span
                      key={`${c}-${i}`}
                      className="d-inline-flex align-items-center gap-1"
                    >
                      <ProductColorDot colorName={c} />
                      <span className="fs-10">{c}</span>
                    </span>
                  ))}
                  <Badge
                    bg={
                      branchDrums.length >= neededCount ? 'success' : 'warning'
                    }
                    variant="phoenix"
                    className="fs-10"
                  >
                    {branchDrums.length} of {neededCount} selected
                  </Badge>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── 3. how much ───────────────────────────────────────────────── */}
        <h5 className="mb-2">How much</h5>
        <div className="d-flex flex-wrap align-items-center gap-4 mb-3">
          <Form.Check
            type="radio"
            id="mode-total"
            name="transform-mode"
            label={`Total — convert the whole product (${fmt(srcJob.plannedLength)} m, length unchanged)`}
            checked={mode === 'total'}
            onChange={() => setMode('total')}
          />
          <Form.Check
            type="radio"
            id="mode-partial"
            name="transform-mode"
            label={`Partial — split off a length into the new product (keep the rest as ${srcJob.name})`}
            checked={mode === 'partial'}
            onChange={() => setMode('partial')}
          />
        </div>
        {mode === 'partial' && (
          <Row className="g-3 mb-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Fork length (m)</Form.Label>
                <Form.Control
                  type="number"
                  value={forkLength}
                  max={maxFork}
                  onChange={e => {
                    let v = toNum(e.target.value);
                    if (v < 0) v = 0;
                    if (v > maxFork) v = maxFork;
                    setForkLength(v ? String(v) : '');
                  }}
                  placeholder={`≤ ${fmt(maxFork)}`}
                />
              </Form.Group>
            </Col>
            <Col md={9}>
              <div className="fs-9 text-700">
                {branchDrums.length} <b>{PHASE_LABEL[branchPhase] || branchPhase}</b>{' '}
                drum(s) selected, max {fmt(maxFork)} m each. Every selected drum
                gives the same {fmt(toNum(forkLength))} m — parallel conductors
                have to stay the same length.
              </div>
              {branchIsPending && (
                <div className="fs-10 text-info mt-1">
                  <b>Merge-execution</b>: the shared phases are still queued →
                  they’ll be split into a merged pair sharing one bobbin and run
                  together, then diverge into {target.name}.
                </div>
              )}
            </Col>
          </Row>
        )}

        {incompleteSelection && (
          <Alert variant="warning" className="py-2 fs-9">
            <b>
              {branchDrums.length} of {neededCount} conductors selected
            </b>{' '}
            — {targetLabel} needs all {neededCount}
            {missingColors.length ? (
              <>
                {' '}
                (missing{' '}
                {missingColors.map((c, i) => (
                  <span key={c} className="d-inline-flex align-items-center gap-1">
                    <ProductColorDot colorName={c} />
                    <b>{c}</b>
                    {i < missingColors.length - 1 ? ', ' : ''}
                  </span>
                ))}
                )
              </>
            ) : null}
            . The new product will own only the drums ticked above, so its{' '}
            <b>rope cannot be planned</b> until the missing conductors are
            forked into it as well — and there is no way to add them afterwards.
            Fork all {neededCount} together unless you specifically want a
            single-conductor lot.
          </Alert>
        )}

        {/* ── 4. orders ─────────────────────────────────────────────────── */}
        <h5 className="mb-1 mt-4">Orders &amp; reservations</h5>
        {allocBusy && (
          <p className="text-700 fs-9">
            <Spinner animation="border" size="sm" className="me-2" />
            Looking up what this product is promised to…
          </p>
        )}
        {allocErr && (
          <Alert variant="danger" className="py-2 fs-9">
            Could not read the allocations: {allocErr}. Transform anyway and the
            orders stay exactly as they are.
          </Alert>
        )}
        {!allocBusy && !allocErr && !allocLines.length && (
          <p className="text-700 fs-9">
            Not allocated to any order — this is stock, so there is nothing to
            decide.
          </p>
        )}
        {!!allocLines.length && (
          <>
            <p className="text-700 fs-9 mb-2">
              {mode === 'total'
                ? 'The whole lot becomes a different cable, so every line promised this product has to say what it wants.'
                : `${fmt(toNum(forkLength))} m leave this product and start as STOCK — a different cable cannot fulfil a line that asked for this one. What is decided here is how much of its promise ${srcJob.name} keeps.`}
            </p>

            {mode === 'partial' && (
              <Alert
                variant={overPromised > 0 ? 'danger' : 'success'}
                className="py-2 fs-9"
              >
                <b>{srcJob.name}</b> can make <b>{fmt(capacityAfter)} m</b> after
                this fork, and still promises <b>{fmt(promisedAfter)} m</b>.
                {overPromised > 0 ? (
                  <>
                    {' '}
                    Over by <b>{fmt(overPromised)} m</b> — trim below before this
                    can be submitted.
                    <Button
                      size="sm"
                      variant="phoenix-secondary"
                      className="ms-2"
                      onClick={autoTrim}
                    >
                      Auto-trim to capacity
                    </Button>
                  </>
                ) : (
                  ' That fits.'
                )}
              </Alert>
            )}

            {allocLines.map(l => {
              const k = `${l.orderId}::${l.lineId}`;
              const d = decisions[k] || { action: 'KEEP', keep: {}, reduceMeters: 0 };
              const owed = toNum(l.allocatedFromThisJob);
              const items = l.deliveryItems || [];
              const itemised = toNum(l.reservedUnits) > 0;
              const keptMeters = items.reduce(
                (s, it) =>
                  s + toNum((d.keep || {})[it.itemId]) * toNum(it.lengthPerUnit),
                0
              );
              return (
                <div key={k} className="border rounded-2 p-3 mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Badge
                      bg="primary"
                      variant="phoenix"
                      className="fs-10 font-monospace"
                    >
                      {l.orderCode || l.orderId}
                    </Badge>
                    <span className="fw-semibold">{l.client || '—'}</span>
                    <span className="text-700 fs-10">{l.productLabel || '—'}</span>
                    {l.dueDate && (
                      <span className="text-700 fs-10">
                        due {String(l.dueDate).slice(0, 10)}
                      </span>
                    )}
                    <span className="ms-auto fs-9">
                      <b>{fmt(owed)} m</b>{' '}
                      <span className="text-700">promised by this product</span>
                      {itemised && (
                        <span className="text-700">
                          {' '}· {l.reservedUnits} unit(s) itemised
                        </span>
                      )}
                      {l.reserved && (
                        <Badge bg="info" variant="phoenix" className="ms-2 fs-10">
                          reserved
                        </Badge>
                      )}
                    </span>
                  </div>

                  {(l.orderMissing || l.lineMissing) && (
                    <Alert variant="warning" className="py-1 px-2 fs-10 mb-2">
                      {l.orderMissing
                        ? 'This order no longer exists'
                        : 'This line is no longer on the order'}{' '}
                      — the allocation is stale. Releasing it is the only thing
                      that will do anything.
                    </Alert>
                  )}

                  {mode === 'total' ? (
                    <div className="d-flex flex-column gap-1 fs-9">
                      <Form.Check
                        type="radio"
                        name={`alloc-${k}`}
                        id={`follow-${k}`}
                        checked={d.action === 'FOLLOW'}
                        onChange={() => setDecision(k, { action: 'FOLLOW' })}
                        label={
                          <>
                            Order follows the new product — the line is rewritten
                            to <b>{targetLabel}</b>
                          </>
                        }
                      />
                      <Form.Check
                        type="radio"
                        name={`alloc-${k}`}
                        id={`release-${k}`}
                        checked={d.action === 'RELEASE'}
                        onChange={() => setDecision(k, { action: 'RELEASE' })}
                        label={`Release the ${fmt(owed)} m — the line goes back to looking for cable`}
                      />
                      <Form.Check
                        type="radio"
                        name={`alloc-${k}`}
                        id={`keep-${k}`}
                        checked={d.action === 'KEEP'}
                        onChange={() => setDecision(k, { action: 'KEEP' })}
                        label="Leave it — I will reconcile this in Sales"
                      />
                    </div>
                  ) : itemised ? (
                    <>
                      <div className="text-700 fs-10 mb-1">
                        How many of each stay with {srcJob.name}:
                      </div>
                      <Table size="sm" className="fs-10 align-middle mb-1">
                        <thead>
                          <tr className="text-uppercase text-700">
                            <th>Delivery item</th>
                            <th className="text-end">Each</th>
                            <th className="text-end">Promised</th>
                            <th className="text-end" style={{ width: 110 }}>
                              Keeps
                            </th>
                            <th className="text-end">Released</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(it => {
                            const have = toNum(it.reserved);
                            const kept = toNum((d.keep || {})[it.itemId]);
                            const label =
                              String(it.kind).toUpperCase() === 'COIL'
                                ? 'coils'
                                : `${it.drumMaterial || 'drum'}${it.drumDiameter ? ` Ø${it.drumDiameter}` : ''}`;
                            return (
                              <tr key={it.itemId}>
                                <td>{label}</td>
                                <td className="text-end">
                                  {fmt(it.lengthPerUnit)} m
                                </td>
                                <td className="text-end">{have}</td>
                                <td>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    className="text-end"
                                    value={kept}
                                    max={have}
                                    onChange={e => {
                                      let v = Math.floor(toNum(e.target.value));
                                      if (v < 0) v = 0;
                                      if (v > have) v = have;
                                      setDecision(k, {
                                        keep: { ...(d.keep || {}), [it.itemId]: v }
                                      });
                                    }}
                                  />
                                </td>
                                <td className="text-end">
                                  {have - kept > 0 ? (
                                    <span className="text-warning fw-semibold">
                                      {have - kept}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="fs-10 text-700">
                        Keeps <b>{fmt(keptMeters)} m</b> of the {fmt(owed)} m it
                        owed;{' '}
                        <b>{fmt(Math.max(0, owed - keptMeters))} m</b> go back to
                        the order.
                      </div>
                    </>
                  ) : (
                    <Row className="g-2 align-items-end">
                      <Col md={3}>
                        <Form.Label className="fs-10 mb-1">
                          Metres to hand back
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={d.reduceMeters}
                          max={owed}
                          onChange={e => {
                            let v = toNum(e.target.value);
                            if (v < 0) v = 0;
                            if (v > owed) v = owed;
                            setDecision(k, { reduceMeters: v });
                          }}
                        />
                      </Col>
                      <Col className="fs-10 text-700">
                        No drums or coils are itemised on this line yet — its
                        split is still decided at the final phase, so only the
                        metres can move. Keeps{' '}
                        <b>{fmt(Math.max(0, owed - toNum(d.reduceMeters)))} m</b>.
                      </Col>
                    </Row>
                  )}
                </div>
              );
            })}
            {!allocationDecisions.length && (
              <div className="text-700 fs-10 mb-2">
                Nothing decided — the orders will be left exactly as they are.
              </div>
            )}
          </>
        )}

        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" onClick={() => navigate(returnTo)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
            {busy
              ? 'Working…'
              : mode === 'total'
                ? 'Convert → plan downstream'
                : 'Split / Fork → plan downstream'}
          </Button>
        </div>
        <div className="text-700 fs-10 mt-2">
          Copper stays counted once (same conductors); the target recalculates
          its own sheath / armour.
        </div>
      </Card.Body>
    </Card>
  );
};

export default TransformWizzard;
